import React, { useState, useRef, useEffect, useMemo } from "react";
import CodeEditor from "./Editor";
import ConsolePanel from "./ConsolePanel";
import Preview from "./Preview";
import QuestionsPanel from "./QuestionsPanel";
import LessonCard from "../contents/lessonCards";
import TutorialPlayer from "./TutorialPlayer";
import tutorialHTMLData from "../content/intro-html.json";
import questionsHtml from "../content/questions-html.json"
import tutorialCSSData from "../content/intro-css.json";
import questionsCss from "../content/questions-css.json"
import tutorialJSData from "../content/intro-js.json";
import questionsJs from "../content/questions-js.json";
import bgImg from '../assets/drawing.svg';
import { Play, ChevronDown } from "lucide-react";

const THEMES = [
  "vs",             // built-in light
  "vs-dark",        // built-in dark
  "hc-black",       // built-in high contrast
  "github",
  "dracula",
  "monokai",
  "solarized-dark",
  "solarized-light",
];

export default function LMSPlayground({
  html,
  css,
  js,
  setHtml,
  setCss,
  setJs,
  editorTheme,
  runKey,
  setGetPreviewUrlFn,
  lang,
  setLang,
  panelLayout,
  isProjectsOpen,
  setIsProjectsOpen,
  setRunKey,
  setEditorTheme,
  getPreviewUrlFn,
}) {
  const editorRef = useRef(null);
  const [rightTab, setRightTab] = useState("preview");
  const [consoleEntries, setConsoleEntries] = useState([]);
  const [view, setView] = useState("ppt");
  const [lessonType, setLessonType] = useState("html");
  const [openMenu, setOpenMenu] = useState(null);
  const themeMenuRef = useRef(null);
  const editorMenuRef = useRef(null);

  const currentCode = lang === "html" ? html : lang === "css" ? css : js;
  const setCurrentCode = (v) => {
    if (lang === "html") setHtml(v);
    else if (lang === "css") setCss(v);
    else setJs(v);
  };

  const toggleMenu = (menu) => {
    setOpenMenu((prev) => (prev === menu ? null : menu));
  };

  const lessonsMap = {
    html: { questions: questionsHtml, tutorial: tutorialHTMLData },
    css: { questions: questionsCss, tutorial: tutorialCSSData },
    js: { questions: questionsJs, tutorial: tutorialJSData },
  };

  useEffect(() => {
    const handler = (event) => {
      // basic shape check for messages coming from preview iframe
      if (!event.data || event.data?.source !== "preview") return;
      const { type, message } = event.data;
      if (!type || message === undefined) return;
      const t = type === "error" ? "error" : type === "warn" ? "warn" : type === "info" ? "info" : "log";
      setConsoleEntries((p) => [...p, { type: t , message: String(message), ts: Date.now() }]);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  function quickHtmlChecks(html) {
    const entries= [];
    // Unclosed quotes in attributes
    const attrRegex = /\w+="[^"]*$/m;
    if (attrRegex.test(html)) {
      entries.push({
        type: "error",
        message: "HTML attribute value missing closing quote",
        ts: Date.now(),
        source: "html",
      });
    }
    return entries;
  }

  async function lintAll(html, css) {
    const newEntries = [];

    // Quick HTML checks
    const quickEntries = quickHtmlChecks(html);
    newEntries.push(...quickEntries);

    // HTMLHint
    try {
      const htmlModule = await import("htmlhint");
      const HTMLHint = (htmlModule).HTMLHint ?? htmlModule;
      const rules = {
        "tag-pair": true,
        "attr-lowercase": true,
        "id-unique": true,
        "attr-value-double-quotes": true,
      };
      const htmlProblems = HTMLHint.verify(html, rules);
      for (const p of htmlProblems) {
        newEntries.push({
          type: "error",
          message: `HTML: ${p.message}`,
          ts: Date.now(),
          source: "html",
          line: p.line,
          col: p.col,
          rule: p.rule?.id,
        });
      }
    } catch (e) {
      newEntries.push({
        type: "warn",
        message: `HTML linter failed: ${String(e?.message ?? e)}`,
        ts: Date.now(),
        source: "html",
      });
    }

    // CSSLint (unchanged)
    try {
      const cssModule = await import("csslint");
      const CSSLint = (cssModule).CSSLint ?? cssModule;
      const cssResult = CSSLint.verify(css, {
        "known-properties": true,
        "order-alphabetical": false,
        "box-model": false,
        "import": false,
      });
      const seen = new Set();
      for (const m of cssResult.messages || []) {
        const key = `${m.line}:${m.col}:${m.message}`;
        if (seen.has(key)) continue;
        seen.add(key);

        newEntries.push({
          type: m.type === "error" ? "error" : "warn",
          message: `CSS: ${m.message}`,
          ts: Date.now(),
          source: "css",
          line: m.line,
          col: m.col,
          rule: m.rule?.id ?? m.rule?.name,
        });
      }
    } catch (e) {
      newEntries.push({
        type: "warn",
        message: `CSS linter failed: ${String(e?.message ?? e)}`,
        ts: Date.now(),
        source: "css",
      });
    }

    return newEntries;
  }

  useEffect(() => {
    (async () => {
      const results = await lintAll(html, css);
      setConsoleEntries((prev) => [...prev, ...results]);
    })();
  }, [runKey, html, css]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        openMenu &&
        !editorMenuRef.current?.contains(event.target) &&
        !themeMenuRef.current?.contains(event.target)
      ) {
        setOpenMenu(null);
      }
    }
    if (openMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenu]);

  const clearConsole = () => setConsoleEntries([]);

  (window).getCurrentEditorContent = () => {
    return { html, css, js };
  };

  // Enhanced validator that automatically detects which code to check
  (window).validateCurrentEditor = async (task) => {
    const { html, css, js } = (window).getCurrentEditorContent();
    const v = task.validator || { type: "contains", args: "" };
    
    // Smart code selection: determine which code to validate based on the validator pattern
    function getCodeToValidate() {
      // If task.lang is explicitly set, use it and switch the tab
      if (task.lang) {
        const targetLang = task.lang === "css" ? "css" : task.lang === "js" ? "js" : "html";
        setLang(targetLang); // Auto-switch tab
        return task.lang === "css" ? css : task.lang === "js" ? js : html;
      }
      
      // Auto-detect based on validator pattern and switch tab accordingly
      const pattern = v.args;
      
      // CSS patterns
      if (typeof pattern === "string") {
        // CSS selectors and properties
        if (pattern.includes("{") || 
            pattern.includes("color:") || 
            pattern.includes("background") ||
            pattern.includes("font-") ||
            pattern.includes("margin") ||
            pattern.includes("padding") ||
            pattern.includes("border") ||
            pattern.match(/\w+\s*\{/) || // CSS selector patterns like "h1 {"
            pattern.match(/:\s*\w+/) || // CSS property patterns like ": red"
            pattern.includes("px") ||
            pattern.includes("em") ||
            pattern.includes("rem")) {
          setLang("css"); // Auto-switch to CSS tab
          return css;
        }
        
        // JavaScript patterns
        if (pattern.includes("function") ||
            pattern.includes("console.log") ||
            pattern.includes("document.") ||
            pattern.includes("window.") ||
            pattern.includes("addEventListener") ||
            pattern.includes("querySelector") ||
            pattern.includes("var ") ||
            pattern.includes("let ") ||
            pattern.includes("const ") ||
            pattern.includes("=>") ||
            pattern.includes("alert(")) {
          setLang("js"); // Auto-switch to JS tab
          return js;
        }
        
        // HTML patterns (default)
        if (pattern.includes("<") || pattern.includes(">") || pattern.includes("&lt;") || pattern.includes("&gt;")) {
          setLang("html"); // Auto-switch to HTML tab
          return html;
        }
      }
      
      // Default to HTML for ambiguous cases
      setLang("html"); // Auto-switch to HTML tab
      return html;
    }
    
    const code = getCodeToValidate();
    const codeType = code === css ? "CSS" : code === js ? "JavaScript" : "HTML";
    
    if (v.type === "contains") {
      const needle = v.args;
      
      // Enhanced HTML validation for flexible matching
      if (code === html && needle.includes('<') && needle.includes('>')) {
        const match = needle.match(/<(\w+)([^>]*)>(.*?)<\/\1>/s);
        if (match) {
          const [, tagName, , content] = match;
          const contentTrimmed = content.trim();
          
          const flexiblePattern = new RegExp(
            `<${tagName}(?:\\s+[^>]*)?\\s*>\\s*${contentTrimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*<\\/${tagName}>`,
            'is'
          );
          
          const ok = flexiblePattern.test(code);
          return { 
            valid: ok, 
            reason: ok ? undefined : `Expected to find <${tagName}> element with content "${contentTrimmed}" in your HTML` 
          };
        }
      }
      
      // Fallback to original contains logic
      const ok = code.includes(needle);
      return { 
        valid: ok, 
        reason: ok ? undefined : `Expected to find "${needle}" in your ${codeType} code` 
      };
    }

    if (v.type === "regex") {
      const re = new RegExp(v.args, "mi"); // Added 'i' flag for case-insensitive matching
      const ok = re.test(code);
      return { 
        valid: ok, 
        reason: ok ? undefined : `Pattern not found in your ${codeType} code. ${getHintForPattern(v.args, codeType)}` 
      };
    }

    // CSS-specific validator
    if (v.type === "css-rule") {
      const { selector, property, value } = v.args;
      
      // Simple CSS rule checker
      const rulePattern = new RegExp(
        `${selector.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\{[^}]*${property}\\s*:\\s*${value ? value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '[^;}]+'}`,
        'i'
      );
      
      const ok = rulePattern.test(css);
      return {
        valid: ok,
        reason: ok ? undefined : `Expected CSS rule: ${selector} { ${property}: ${value || 'some value'}; }`
      };
    }

    // JavaScript-specific validator
    if (v.type === "js-function") {
      const { name, content } = v.args;
      const functionPattern = new RegExp(`function\\s+${name}|${name}\\s*=\\s*function|const\\s+${name}\\s*=`, 'i');
      const ok = functionPattern.test(js) && (!content || js.includes(content));
      return {
        valid: ok,
        reason: ok ? undefined : `Expected JavaScript function "${name}"${content ? ` containing "${content}"` : ''}`
      };
    }

    if (v.type === "html-element") {
      const { tag, content, attributes } = v.args;
      
      try {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument;
        doc.open();
        doc.write(html); // Always use HTML for DOM checks
        doc.close();
        
        const elements = doc.querySelectorAll(tag);
        let found = false;
        
        for (const el of elements) {
          let matches = true;
          
          if (content && el.textContent?.trim() !== content.trim()) {
            matches = false;
          }
          
          if (attributes && matches) {
            for (const [attr, value] of Object.entries(attributes)) {
              if (value === true && !el.hasAttribute(attr)) {
                matches = false;
                break;
              } else if (typeof value === "string" && el.getAttribute(attr) !== value) {
                matches = false;
                break;
              }
            }
          }
          
          if (matches) {
            found = true;
            break;
          }
        }
        
        document.body.removeChild(iframe);
        return { 
          valid: found, 
          reason: found ? undefined : `Expected to find <${tag}> element${content ? ` with content "${content}"` : ""}${attributes ? " with specified attributes" : ""}` 
        };
      } catch (e) {
        return { valid: false, reason: String(e) };
      }
    }

    if (v.type === "dom-check") {
      try {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument;
        doc.open();
        doc.write(html); // Always use HTML for DOM checks
        doc.close();
        
        const { selector, text } = v.args;
        const el = doc.querySelector(selector);
        const ok = !!el && (!text || el.textContent?.trim() === text?.trim());
        document.body.removeChild(iframe);
        return { 
          valid: ok, 
          reason: ok ? undefined : `DOM check failed: ${selector}${text ? ` with text="${text}"` : ""}` 
        };
      } catch (e) {
        return { valid: false, reason: String(e) };
      }
    }

    return { valid: false, reason: "Unknown validator type" };
  };

  // Helper function to provide hints based on the pattern and code type
  function getHintForPattern(pattern, codeType) {
    if (codeType === "CSS") {
      if (pattern.includes("color")) {
        return "💡 Try adding a color property like 'color: red;'";
      }
      if (pattern.includes("h1")) {
        return "💡 Try styling the h1 element with CSS rules";
      }
    }
    
    if (codeType === "HTML") {
      if (pattern.includes("li")) {
        return "💡 Try adding list items with <li> tags";
      }
      if (pattern.includes("ul")) {
        return "💡 Try creating an unordered list with <ul> tags";
      }
      if (pattern.includes("p")) {
        return "💡 Try adding a paragraph with <p> tags";
      }
    }
    
    return "";
  }

  const run = async () => {
    setRunKey((k) => k + 1);
  };

  const themeMenu = useMemo(() => (
    <div>
      {THEMES.map((theme) => (
        <button
          key={theme}
          className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100 capitalize"
          onMouseDown={() => {
            setEditorTheme(theme);
            setOpenMenu(null);
          }}
        >
          {theme.replace("-", " ")}
        </button>
      ))}
    </div>
  ), []);
  
  const jumpToEntry = (entry) => {
    if (!entry.line) return;
    // set editor language if necessary
    if (entry.source === "html") setLang("html");
    else if (entry.source === "css") setLang("css");
    else if (entry.source === "js") setLang("js");

    const ed = editorRef.current;
    if (!ed) return;
    // Monaco expects {lineNumber, column}
    const line = entry.line ?? 1;
    const col = entry.col ?? 1;

    // set position & reveal
    try {
      ed.setPosition({ lineNumber: line, column: col });
      ed.revealPositionInCenter({ lineNumber: line, column: col });
      ed.focus();
    } catch (err) {
      // older versions or different shape — ignore
      console.warn("couldn't jump to editor position", err);
    }
  };

  // Render components based on panel layout
  const renderLeftPanel = () => {
    const current = lessonsMap[lessonType];

    return (
      <div className="flex-1 rounded-lg overflow-hidden bg-indigo-300 border border-gray-200 shadow-sm flex flex-col my-3 ml-3">
        <LessonCard
          title="Lesson Content"
          actions={
            <>
              <button
                onClick={() => setView("questions")}
                className={`px-2 py-1 text-sm rounded cursor-pointer ${
                  view === "questions"
                    ? "text-indigo-600 font-bold bg-white border border-gray-200"
                    : "font-normal"
                }`}
              >
                Questions
              </button>
              <button
                onClick={() => setView("ppt")}
                className={`px-2 py-1 rounded text-sm cursor-pointer ${
                  view === "ppt"
                    ? "text-indigo-600 font-bold bg-white border border-gray-200"
                    : "font-normal"
                }`}
              >
                PPT
              </button>
            </>
          }
        >
          {view === "questions" ? (
            <QuestionsPanel
              questions={current.questions}
              onLoadCode={(html, css, js) => {
                setHtml(html);
                setCss(css);
                setJs(js);

                if (js && js.trim()) setLang("js");
                else if (css && css.trim()) setLang("css");
                else if (html && html.trim()) setLang("html");
                else setLang("html");
              }}
              setLang={setLang}
            />
          ) : (
            <TutorialPlayer
              tutorial={current.tutorial}
              onLoadCode={([html, css, js]) => {
                setHtml(html);
                setCss(css);
                setJs(js);

                if (js && js.trim()) setLang("js");
                else if (css && css.trim()) setLang("css");
                else if (html && html.trim()) setLang("html");
                else setLang("html");
              }}
            />
          )}
        </LessonCard>
      </div>
    );
  };

  const renderEditorPanel = () => (
    <div className={`${panelLayout === "three-panel" ? "flex-1" : "flex-1"} rounded-lg overflow-hidden bg-indigo-300 border border-gray-200 shadow-sm flex flex-col my-3 ${panelLayout === "editor-preview" ? "ml-3": ""}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          {lang === "html" ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height='24px' width='24px'><path fill="#ff841f" d="M128 96L162.9 491.8L319.5 544L477.1 491.8L512 96L128 96zM436.2 223.9L252.4 223.9L256.5 273.3L432.1 273.3L418.5 421.7L320.6 448.7L320.6 449L319.5 449L220.8 421.7L214.8 345.9L262.5 345.9L266 384L319.5 398.5L373.2 384L379.2 321.8L212.3 321.8L199.5 176.2L440.6 176.2L436.2 223.9z"/></svg>
          ): lang === "css" ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height='24px' width='24px'><path fill="#351fffff" d="M128 96L162.9 491.8L320 544L477.1 491.8L512 96L128 96zM441.1 176L436.3 223.3L321 272.6L320.7 272.7L432.2 272.7L419.4 419.3L321.2 448L222.4 418.8L216 344.9L264.9 344.9L268.1 383.2L320.7 396.5L375.4 381.1L379.1 319.5L212.8 319L212.8 318.9L212.6 319L209 272.7L321.1 226L327.6 223.3L204.7 223.3L198.9 176L441.1 176z"/></svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height='24px' width='24px'><rect x="96" y="96" width="448" height="448" fill="#ffdd1fff"/><path fill="black" d="M339.8 445.4C339.8 489 314.2 508.9 276.9 508.9C243.2 508.9 223.7 491.5 213.7 470.4L248 449.7C254.6 461.4 260.6 471.3 275.1 471.3C288.9 471.3 297.7 465.9 297.7 444.8L297.7 301.7L339.8 301.7L339.8 445.4zM439.4 508.9C400.3 508.9 375 490.3 362.7 465.9L397 446.1C406 460.8 417.8 471.7 438.5 471.7C455.9 471.7 467.1 463 467.1 450.9C467.1 436.5 455.7 431.4 436.4 422.9L425.9 418.4C395.5 405.5 375.4 389.2 375.4 354.9C375.4 323.3 399.5 299.3 437 299.3C463.8 299.3 483 308.6 496.8 333L464 354C456.8 341.1 449 336 436.9 336C424.6 336 416.8 343.8 416.8 354C416.8 366.6 424.6 371.7 442.7 379.6L453.2 384.1C489 399.4 509.1 415.1 509.1 450.3C509.1 488.1 479.3 508.9 439.4 508.9z"/></svg>
          )}
          <div className="text-sm font-medium uppercase">
            {lang}
          </div>
        </div>
        <div className="text-xs text-gray-500 flex flex-row gap-1">
          <div className="flex flex-row gap-1">
            <p className="text-xs text-white my-auto">Editor:</p>
            <div className="relative" ref={editorMenuRef}>
              <button
                onClick={() => toggleMenu("editor")}
                className="px-3 py-1.5 bg-white rounded-lg flex items-center cursor-pointer gap-1 hover:bg-grey-200"
              >
                {lang} <ChevronDown size={16} />
              </button>
              <div className={`absolute top-full left-0 mt-2 w-40 bg-white shadow-lg rounded-lg border z-50 transition-all duration-300
              ${openMenu === "editor"? "max-h-[20rem] opacity-100 overflow-hidden" : "max-h-0 opacity-0 z-[-10]"}`}>
                <button 
                  className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                  onMouseDown={() => {
                    setLang("html");
                    setOpenMenu(null);
                  }}
                >
                  HTML
                </button>
                <button 
                  className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                  onMouseDown={() => {
                    setLang("css");
                    setOpenMenu(null);
                  }}
                >
                  CSS
                </button>
                <button 
                  className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                  onMouseDown={() => {
                    setLang("js");
                    setOpenMenu(null);
                  }}
                >
                  JavaScript
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-row gap-2 items-center">
            <p className="text-xs text-white my-auto">Theme:</p>
            <div className="relative" ref={themeMenuRef}>
              <button
                onClick={() => {toggleMenu("theme")}}
                className="px-3 py-1.5 rounded-lg border bg-white flex items-center gap-1 cursor-pointer hover:bg-grey-700"
              >
                {editorTheme} <ChevronDown size={16} />
              </button>
              <div className={`absolute top-full left-0 mt-2 w-40 bg-white shadow-lg rounded-lg border z-50 transition-all duration-300
                ${openMenu === "theme"? "max-h-[20rem] opacity-100 overflow-hidden" : "max-h-0 opacity-0 z-[-10]"}`}>
              {themeMenu}
              </div>
            </div>
          </div>
          <button
            onClick={run}
            className="inline-flex items-center gap-2 px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 cursor-pointer text-white text-sm shadow"
            title="Run code"
          >
            <Play size={14} />
            <span>Run</span>
          </button>
          </div>
      </div>
      <div className="flex-1 min-h-0">
        <CodeEditor
          language={lang === "js" ? "javascript" : lang}
          code={currentCode ?? ""}
          onChange={setCurrentCode}
          theme={editorTheme}
          onEditorMount={(ed) => editorRef && (editorRef.current = ed)}
        />
      </div>
    </div>
  );

  const renderRightPanel = () => (
    <div className="w-96 flex flex-col gap-2 my-3 mr-3">
      <div className="rounded-lg overflow-hidden bg-indigo-300 border border-gray-200 shadow-sm flex flex-col h-full">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
          <div>
            <button
              onClick={() => setRightTab("preview")}
              className={`px-3 py-1 rounded text-sm cursor-pointer ${rightTab === "preview" ? "bg-gray-100 text-gray-900" : "text-gray-500"}`}
            >
              Preview
            </button>
            <button
              onClick={() => setRightTab("console")}
              className={`px-3 py-1 rounded text-sm cursor-pointer ${rightTab === "console" ? "bg-gray-100 text-gray-900" : "text-gray-500"}`}
            >
              Console{" "}
              {consoleEntries.length > 0 && (
                <span className="ml-2 text-xs bg-red-500 text-white rounded-full px-2">{consoleEntries.length}</span>
              )}
            </button>
          </div>
          <div className="text-xs text-gray-500">
            <button
              className="px-2 py-1 border cursor-pointer rounded bg-white"
              title="Show Preview in new tab"
              onClick={() => {
                if (!getPreviewUrlFn) return;

                // generate a fresh Blob URL
                const url = getPreviewUrlFn();
                if (!url) return;

                const win = window.open(url, "_blank");
                if (!win) return; // just in case popups are blocked

                // revoke after a short delay so tab can load
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              }}
            >
              Open in new tab
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 relative">
          <div
            className={`absolute inset-0 ${rightTab === "preview" ? "block" : "hidden"}`}
          >
            <Preview key={runKey} html={html} css={css} js={js} runKey={runKey} getPreviewUrl={setGetPreviewUrlFn} />
          </div>

          <div
            className={`absolute inset-0 ${rightTab === "console" ? "block" : "hidden"}`}
          >
            <ConsolePanel entries={consoleEntries} onClear={clearConsole} onEntryClick={jumpToEntry} />
          </div>
        </div>
      </div>
    </div>
  );

  const availableFiles = [
    {
      name: "HTML Lesson",
      type: "lesson",
      onClick: () => {
        setLessonType("html");
        setIsProjectsOpen(false);
      },
    },
    {
      name: "CSS Lesson",
      type: "lesson",
      onClick: () => {
        setLessonType("css");
        setIsProjectsOpen(false);
      },
    },
    {
      name: "JS Lesson",
      type: "lesson",
      onClick: () => {
        setLessonType("js");
        setIsProjectsOpen(false);
      },
    },
  ];

  const renderSidePanel = () => (
    <div
      className={`fixed top-0 left-0 h-full z-40 transition-all duration-300 bg-white shadow-lg border-r border-gray-200 ${
        isProjectsOpen ? "w-96" : "w-0"
      } overflow-hidden`}
      style={{ minHeight: "100vh" }}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-bold text-lg">Add New Files</div>
          <button
            className="text-gray-500 hover:text-gray-800"
            onClick={() => setIsProjectsOpen(false)}
          >
            ✕
          </button>
        </div>
        <div className="flex-1 px-4 py-4">
          <ul>
            {availableFiles.map((file) => (
              <li key={file.name} className="mb-4">
                <button
                  className="w-full text-left px-4 py-2 rounded bg-indigo-100 hover:bg-indigo-200 font-medium"
                  onClick={file.onClick}
                >
                  {file.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div 
      className="relative h-full w-full"
      style={{
        backgroundImage: `url(${bgImg})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Side panel */}
      {renderSidePanel()}

      {/* Main playground layout */}
      <div className="flex flex-row gap-3 overflow-hidden h-[calc(100vh-55px)] animated-background">
        {panelLayout === "questions-editor" && (
          <>
            {renderLeftPanel()}
            {renderEditorPanel()}
          </>
        )}
        {panelLayout === "editor-preview" && (
          <>
            {renderEditorPanel()}
            {renderRightPanel()}
          </>
        )}
        {panelLayout === "three-panel" && (
          <>
            {renderLeftPanel()}
            {renderEditorPanel()}
            {renderRightPanel()}
          </>
        )}
      </div>
    </div>
  );
}